import {
  getSDK,
  type ActionCard,
  type ActionCardPlacement,
} from '@littlebird/sdk';
import { useEffect, useMemo } from 'react';

import { useInViewport } from '@/hooks/useInViewport';
import { useTabVisible } from '@/hooks/useTabVisible';
import { useWindowFocused } from '@/hooks/useWindowFocused';

import { trackCardViewed } from '../analytics';

// A row counts as shown once it has kept its place on screen this long. A list redraws
// in steps while it loads and while a card leaves, and those steps are shorter
export const SHOWN_AFTER_MS = 1_000;

// A row reports its time on screen while it is still there: first after this long, then
// after twice the gap before. A quit or a reload runs no cleanup, so without these
// reports the row would keep no time. With them it loses the last gap at most
export const FIRST_TIME_REPORT_MS = 15_000;

/**
 * Tells PostHog that a Home or Feed row was seen, once it has stayed on screen in a visible
 * window for a moment. A row with its own place in the list also tells the server where the
 * list showed it, and how long it then stays there while the user has the window in front.
 * Returns the ref for the row's outer element.
 */
export const useFeedImpression = (
  card: ActionCard,
  list: ActionCardPlacement['surface'] | undefined,
  placement: ActionCardPlacement | undefined
) => {
  const { ref, inViewport } = useInViewport<HTMLDivElement>();
  const windowVisible = useTabVisible();
  const windowFocused = useWindowFocused();
  const onScreen = inViewport && windowVisible;
  const surface = placement?.surface;
  const position = placement?.position;
  const listSize = placement?.listSize;
  // The lists build a new placement object on every render, so the effects use one that
  // changes only with its values
  const shownAt = useMemo(
    () =>
      surface === undefined || position === undefined || listSize === undefined
        ? undefined
        : { surface, position, listSize },
    [surface, position, listSize]
  );

  useEffect(() => {
    if (!list || !onScreen) return;
    const shown = setTimeout(() => {
      if (shownAt)
        getSDK().stores.actionCards.recordImpression(card.id, shownAt);
      trackCardViewed(card, list, shownAt);
    }, SHOWN_AFTER_MS);
    return () => clearTimeout(shown);
  }, [card, list, shownAt, onScreen]);

  // A window that is visible beside the app the user works in shows the row, and the user
  // is not looking at it. Only time in a focused window counts
  useEffect(() => {
    if (!shownAt || !onScreen || !windowFocused) return;
    const { actionCards } = getSDK().stores;
    let reportedUntil = Date.now();
    let gap = FIRST_TIME_REPORT_MS;
    let nextReport: ReturnType<typeof setTimeout>;
    const reportTime = () => {
      const now = Date.now();
      // More than two gaps since the last report means the computer slept, which is not time on screen
      const onScreenFor = Math.min(now - reportedUntil, 2 * gap);
      actionCards.recordTimeOnScreen(card.id, shownAt, onScreenFor);
      reportedUntil = now;
    };
    const reportAfterGap = () => {
      nextReport = setTimeout(() => {
        reportTime();
        gap *= 2;
        reportAfterGap();
      }, gap);
    };
    reportAfterGap();
    return () => {
      clearTimeout(nextReport);
      reportTime();
    };
  }, [card.id, shownAt, onScreen, windowFocused]);

  return ref;
};
